import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe, Crown, Lock, Check, Copy, ExternalLink, Sparkles, Tag, Brush, Palette } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api-client";
import { useProfile } from "@/hooks/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { WaitlistModal } from "@/components/shared/WaitlistModal";
import { StatusPageDesigner } from "./StatusPageDesigner";

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{2,29}$/;

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 30);
}

export function WhitelabelSettings() {
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const isPro = profile?.is_pro || false;

  // Slug
  const [slug, setSlug] = useState("");
  const [slugSaved, setSlugSaved] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState("");

  // White-label branding
  const [brandName, setBrandName] = useState("");
  const [brandTagline, setBrandTagline] = useState("");
  const [brandSaved, setBrandSaved] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (profile) {
      setSlug((profile as any).status_page_slug || "");
      setBrandName((profile as any).white_label_name || "");
      setBrandTagline((profile as any).white_label_tagline || "");
    }
  }, [profile]);

  const validateSlug = (val: string) => {
    if (!val) { setSlugError(""); return; }
    if (val.length < 3) { setSlugError("Slug must be at least 3 characters."); return; }
    if (!SLUG_REGEX.test(val)) { setSlugError("Only lowercase letters, numbers and hyphens. Must start with a letter/number."); return; }
    setSlugError("");
  };

  const handleSlugChange = (v: string) => {
    const clean = slugify(v);
    setSlug(clean);
    validateSlug(clean);
    setSlugSaved(false);
  };

  const handleSaveSlug = async () => {
    validateSlug(slug);
    if (slugError || (slug && !SLUG_REGEX.test(slug))) return;
    setSlugSaving(true);
    try {
      await api.updateProfile({ status_page_slug: slug || null });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setSlugSaved(true);
      toast.success(slug ? `Status page slug set: /s/${slug}` : "Custom slug removed");
      setTimeout(() => setSlugSaved(false), 3000);
    } catch {
      toast.error("Failed to save slug");
    } finally {
      setSlugSaving(false);
    }
  };

  const handleSaveBrand = async () => {
    setBrandSaving(true);
    try {
      await api.updateProfile({
        white_label_name: brandName || null,
        white_label_tagline: brandTagline || null,
      });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setBrandSaved(true);
      toast.success("White-label branding saved");
      setTimeout(() => setBrandSaved(false), 3000);
    } catch {
      toast.error("Failed to save branding");
    } finally {
      setBrandSaving(false);
    }
  };

  const publicUrl = slug ? `${window.location.origin}/s/${slug}` : null;

  const handleCopy = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-10">
      {/* Custom Slug Section */}
      <div className="space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Tag className="h-4 w-4 text-primary" />
            <h3 className="text-xs font-black uppercase tracking-widest">Custom Status Page Slug</h3>
          </div>
          <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed">
            Set a memorable, branded URL for your public status page. Share it with your users and customers.
          </p>
        </div>

        <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm space-y-4">
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest">Your Custom Slug</Label>
            <div className="flex items-center gap-2">
              <div className="flex items-center border-2 border-foreground overflow-hidden flex-1 shadow-xs">
                <span className="text-[10px] font-black uppercase px-3 py-3 bg-foreground text-background whitespace-nowrap">{window.location.origin}/s/</span>
                <Input
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-company"
                  className="h-11 rounded-none border-0 border-l-2 border-foreground bg-background text-sm font-mono shadow-none focus:ring-0 flex-1"
                  maxLength={30}
                />
              </div>
            </div>
            <AnimatePresence>
              {slugError && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[10px] font-bold text-status-down uppercase"
                >
                  ⚠ {slugError}
                </motion.p>
              )}
            </AnimatePresence>
            <p className="text-[9px] font-bold text-muted-foreground uppercase">
              Lowercase letters, numbers, hyphens only · 3–30 characters
            </p>
          </div>

          {/* Live Preview */}
          {slug && !slugError && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-2 border-primary/30 p-4 bg-primary/5"
            >
              <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-2">Your Status Page URL</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Globe className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-mono font-bold text-foreground truncate">{publicUrl}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="h-8 w-8 flex items-center justify-center border-2 border-foreground hover:bg-foreground hover:text-background transition-all"
                    title="Copy URL"
                  >
                    {copied ? <Check className="h-3 w-3 text-status-up" /> : <Copy className="h-3 w-3" />}
                  </button>
                  <a
                    href={publicUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 flex items-center justify-center border-2 border-foreground hover:bg-foreground hover:text-background transition-all"
                    title="Open status page"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </motion.div>
          )}

          <Button
            onClick={handleSaveSlug}
            disabled={slugSaving || !!slugError}
            className="w-full h-11 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase tracking-widest shadow-xs hover:bg-primary hover:border-primary transition-all"
          >
            {slugSaved ? (
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-status-up" /> URL Saved</span>
            ) : slugSaving ? "Saving..." : (
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Save Custom URL</span>
            )}
          </Button>
        </div>
      </div>

      <div className="h-px bg-foreground/10" />

      {/* White-label Branding */}
      <div className="space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Brush className="h-4 w-4 text-primary" />
            <h3 className="text-xs font-black uppercase tracking-widest">White-label Branding</h3>
          </div>
          <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed">
            Replace UptimeWing branding on your public status page with your own company name and tagline. Your customers will see your brand, not ours.
          </p>
        </div>

        <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm space-y-4">
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest">Company / Brand Name</Label>
            <Input
              value={brandName}
              onChange={(e) => { setBrandName(e.target.value); setBrandSaved(false); }}
              placeholder="Acme Corp"
              className="h-11 rounded-none border-2 border-foreground bg-background text-sm font-bold shadow-xs focus:ring-0"
              maxLength={60}
            />
            <p className="text-[9px] font-bold text-muted-foreground uppercase">Appears in the header and footer of your public status page.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest">Status Page Tagline (Optional)</Label>
            <Input
              value={brandTagline}
              onChange={(e) => { setBrandTagline(e.target.value); setBrandSaved(false); }}
              placeholder="Real-time system status for Acme services"
              className="h-11 rounded-none border-2 border-foreground bg-background text-sm font-bold shadow-xs focus:ring-0"
              maxLength={120}
            />
            <p className="text-[9px] font-bold text-muted-foreground uppercase">Shown as a subtitle on your status page header.</p>
          </div>

          <Button
            onClick={handleSaveBrand}
            disabled={brandSaving}
            className="w-full h-11 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase tracking-widest shadow-xs hover:bg-primary hover:border-primary transition-all"
          >
            {brandSaved ? (
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-status-up" /> Branding Saved</span>
            ) : brandSaving ? "Saving..." : (
              <span className="flex items-center gap-2"><Brush className="h-4 w-4" /> Apply White-label</span>
            )}
          </Button>
        </div>
      </div>

      <div className="h-px bg-foreground/10" />

      {/* Preset Custom Status Page Branding Customizer */}
      <div className={`space-y-5 ${!isPro ? "opacity-50 pointer-events-none select-none" : ""}`}>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Palette className="h-4.5 w-4.5 text-primary" />
            <h3 className="text-xs font-black uppercase tracking-widest">Status Page Designer & Templates</h3>
          </div>
          <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed">
            Customize templates, color schemes, and logos as presets. Save presets to deploy instantly on your status pages.
          </p>
        </div>

        <StatusPageDesigner isPro={isPro} brandName={brandName} tagline={brandTagline} />
      </div>

      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} initialPlan="founder" />
    </div>
  );
}
